import type { ReactNode } from 'react';
import { Card, CardContent, Input, Textarea } from '@/components/ui';
import type { OkdocScenarioProfile, OkdocScenarioResultCopy, OkdocScenarioSubmitAction } from '@/features/okdoc/types';
import { createDefaultOkdocProfile, resolveAppointmentSubmitActions, resolveFeedbackSubmitActions } from '@/features/okdoc/utils/scenarioProfile';

interface OkdocSubmitFlowProfileEditorProps {
  flow: 'feedback_collection' | 'appointment_confirmation';
  value?: OkdocScenarioProfile;
  onChange: (value: OkdocScenarioProfile) => void;
  previewContent?: ReactNode;
}

function buildDefaultResultCopies(flow: 'feedback_collection' | 'appointment_confirmation'): OkdocScenarioResultCopy[] {
  return createDefaultOkdocProfile(flow).result_copies || [];
}

export function OkdocSubmitFlowProfileEditor({
  flow,
  value,
  onChange,
  previewContent,
}: OkdocSubmitFlowProfileEditorProps) {
  const profile = value || createDefaultOkdocProfile(flow);
  const submitActions = flow === 'feedback_collection' ? resolveFeedbackSubmitActions(profile) : resolveAppointmentSubmitActions(profile);
  const defaultResultCopies = buildDefaultResultCopies(flow);

  const updateProfile = (patch: Partial<OkdocScenarioProfile>) => {
    onChange({
      ...profile,
      kind: flow,
      ...patch,
    });
  };

  const updateSubmitAction = (action: string, patch: Partial<OkdocScenarioSubmitAction>) => {
    const nextActions = [...submitActions];
    const actionIndex = nextActions.findIndex((item) => item.action === action);
    if (actionIndex >= 0) {
      nextActions[actionIndex] = { ...nextActions[actionIndex], ...patch, action };
    } else {
      nextActions.push({ action, ...patch });
    }
    updateProfile({ submit_actions: nextActions });
  };

  const updateResultCopy = (action: string, patch: Partial<OkdocScenarioResultCopy>) => {
    const current = profile.result_copies?.length ? [...profile.result_copies] : [...defaultResultCopies];
    const copyIndex = current.findIndex((item) => item.action === action);
    if (copyIndex >= 0) {
      current[copyIndex] = { ...current[copyIndex], ...patch, action };
    } else {
      current.push({ action, ...patch });
    }
    updateProfile({ result_copies: current });
  };

  return (
    <div className="space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Заголовок shell"
          value={profile.shell_title || ''}
          onChange={(e) => updateProfile({ shell_title: e.target.value })}
          placeholder={flow === 'feedback_collection' ? 'Сбор обратной связи' : 'Подтверждение записи'}
        />
        <Input
          label="Подзаголовок shell"
          value={profile.shell_subtitle || ''}
          onChange={(e) => updateProfile({ shell_subtitle: e.target.value })}
          placeholder={
            flow === 'feedback_collection'
              ? 'Ответ автоматически попадёт в систему контроля качества клиники'
              : 'Ответ автоматически попадёт в систему подтверждений клиники'
          }
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Badge"
          value={profile.badge || ''}
          onChange={(e) => updateProfile({ badge: e.target.value })}
          placeholder="Сбор обратной связи"
        />
        <Input
          label="Kind"
          value={flow}
          onChange={() => undefined}
          disabled
        />
      </div>

      <Input
        label="Заголовок landing"
        value={profile.title || ''}
        onChange={(e) => updateProfile({ title: e.target.value })}
        placeholder="Как прошёл визит?"
      />

      <Input
        label="Подзаголовок"
        value={profile.subtitle || ''}
        onChange={(e) => updateProfile({ subtitle: e.target.value })}
        placeholder="Необязательный подзаголовок"
      />

      <Textarea
        label="Описание"
        value={profile.description || ''}
        onChange={(e) => updateProfile({ description: e.target.value })}
        rows={3}
        placeholder="Короткое пояснение для пациента."
      />

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="text-sm font-medium text-neutral-900">Submit-кнопки</div>
          {submitActions.map((item, index) => (
            <div key={item.action || index} className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
              <Input label="Action" value={item.action || ''} onChange={() => undefined} disabled />
              <Input
                label="Текст кнопки"
                value={item.label || ''}
                onChange={(e) => updateSubmitAction(item.action || '', { label: e.target.value })}
                placeholder="Текст кнопки"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {flow === 'feedback_collection' && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="text-sm font-medium text-neutral-900">Тексты результата</div>
            {(profile.result_copies?.length ? profile.result_copies : defaultResultCopies).map((item, index) => (
              <div key={item.action || index} className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
                <Input label="Action" value={item.action || ''} onChange={() => undefined} disabled />
                <Input
                  label="Title"
                  value={item.title || ''}
                  onChange={(e) => updateResultCopy(item.action || '', { title: e.target.value })}
                  placeholder="Спасибо за высокую оценку"
                />
                <Textarea
                  label="Description"
                  value={item.description || ''}
                  onChange={(e) => updateResultCopy(item.action || '', { description: e.target.value })}
                  rows={3}
                  placeholder="Короткий текст после сохранения ответа."
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {previewContent && (
        <div className="space-y-3">
          <div className="text-sm font-medium text-neutral-900">Live preview</div>
          {previewContent}
        </div>
      )}
    </div>
  );
}
